import fetchJSON from "common/utils/fetchJSON";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AppConfigContextType = {
  isLicenceValid: boolean;
  isLicenceSoonToExpire: boolean;
  isLoading: boolean;
  refreshAppConfig: () => Promise<void>;
};

const AppConfigContext = createContext<AppConfigContextType>({
  isLicenceValid: true,
  isLicenceSoonToExpire: false,
  isLoading: true,
  refreshAppConfig: async () => {},
});

type Props = {
  children: React.ReactNode;
};

export function AppConfigProvider({ children }: Props) {
  const [isLicenceValid, setIsLicenceValid] = useState(true);
  const [isLicenceSoonToExpire, setIsLicenceSoonToExpire] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshAppConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetchJSON({
        url: "app-config",
        method: "GET",
      });

      if (res?.data?.attributes) {
        const { isLicenceValid: valid, isLicenceSoonToExpire: soonExpire } =
          res.data.attributes;

        setIsLicenceValid(valid ?? true);
        setIsLicenceSoonToExpire(soonExpire ?? false);
      }
    } catch (error) {
      console.error("Error fetching app-config:", error);
      // On error, assume invalid to be safe
      setIsLicenceValid(false);
      setIsLicenceSoonToExpire(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAppConfig();
  }, [refreshAppConfig]);

  // Ajouter listeners pour refresh au focus/visibility de la fenêtre
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const debouncedRefresh = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        console.log(
          "[AppConfig] Window focused/visible, refreshing license status"
        );
        refreshAppConfig();
      }, 500); // Debounce 500ms
    };

    const handleFocus = () => {
      debouncedRefresh();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        debouncedRefresh();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshAppConfig]);

  const value = useMemo(
    () => ({
      isLicenceValid,
      isLicenceSoonToExpire,
      isLoading,
      refreshAppConfig,
    }),
    [isLicenceValid, isLicenceSoonToExpire, isLoading, refreshAppConfig]
  );

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  return useContext(AppConfigContext);
}
